import { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, addDays, startOfDay, parse, isSameDay } from 'date-fns';
import { createSessionBooking } from '@/utils/sessionBookingsUtils';
import { getBlockedDates } from '@/utils/blockedDatesUtils';
import { incrementBookingsUsed, isSubscriptionApplication, dispatchSubscriptionBookingUpdate } from '@/utils/subscriptionBookingUtils';
import { 
  getUserTimezone, 
  convertToUTC, 
  convertFromUTC,
  convertAvailabilitySlotsToTimezone,
  getTimezoneAbbreviation 
} from '@/utils/timezoneUtils';

interface BookingCalendarProps {
  mentorId: string;
  applicationId: string;
  onBookingComplete: () => void;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

const BookingCalendar = ({ mentorId, applicationId, onBookingComplete }: BookingCalendarProps) => {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [bookingPreferences, setBookingPreferences] = useState<any>(null);
  const [mentorTimezone, setMentorTimezone] = useState<string>('UTC');
  const [menteeTimezone] = useState<string>(getUserTimezone());
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    isSubscription: boolean;
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
  }>({ isSubscription: false });
  const [currentPeriod, setCurrentPeriod] = useState<{
    periodStart: string;
    periodEnd: string;
  } | null>(null);

  useEffect(() => {
    fetchBookingPreferences();
    fetchBlockedDates();
    fetchSubscriptionInfo();
  }, [mentorId, applicationId]);

  useEffect(() => {
    if (subscriptionInfo.isSubscription) {
      fetchCurrentPeriod();
    }
  }, [subscriptionInfo.isSubscription, applicationId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, mentorId, blockedDates]);

  const fetchBookingPreferences = async () => {
    try {
      const { data, error } = await supabase
        .from('booking_preferences')
        .select('*')
        .eq('mentor_id', mentorId)
        .maybeSingle();

      if (error) throw error;
      setBookingPreferences(data);

      // Store mentor's timezone for conversions
      if (data?.timezone) {
        console.log('DEBUG: Mentor timezone:', data.timezone);
        setMentorTimezone(data.timezone);
      }
    } catch (error) {
      console.error('Error fetching booking preferences:', error);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const result = await getBlockedDates(mentorId);
      if (result.success && result.data) {
        setBlockedDates(result.data.map(bd => bd.date));
      }
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const { data: application, error } = await supabase
        .from('applications')
        .select(`
          subscription_start_date,
          subscription_end_date,
          is_subscription_active,
          courses (course_type)
        `)
        .eq('id', applicationId)
        .single();

      if (error) throw error;

      if (application && application.courses) {
        const isSubscription = application.courses.course_type === 'subscription';
        setSubscriptionInfo({
          isSubscription,
          startDate: application.subscription_start_date,
          endDate: application.subscription_end_date,
          isActive: application.is_subscription_active
        });
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    }
  };

  const fetchCurrentPeriod = async () => {
    if (!subscriptionInfo.isSubscription) return;
    
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data: period, error } = await supabase
        .from('subscription_bookings')
        .select('week_start_date, period_end_date')
        .eq('application_id', applicationId)
        .eq('is_active', true)
        .lte('week_start_date', today)
        .gte('period_end_date', today)
        .maybeSingle();

      if (error) {
        console.error('Error fetching current period:', error);
        return;
      }

      if (period) {
        setCurrentPeriod({
          periodStart: period.week_start_date,
          periodEnd: period.period_end_date
        });
      }
    } catch (error) {
      console.error('Error in fetchCurrentPeriod:', error);
    }
  };

  const fetchAvailableSlots = async (date: Date) => {
    try {
      const dayOfWeek = date.getDay();
      const dateString = format(date, 'yyyy-MM-dd');
      
      // Check if date is blocked
      if (blockedDates.includes(dateString)) {
        setAvailableSlots([]);
        return;
      }

      // Get mentor availability for this day
      const { data: availability, error } = await supabase
        .from('mentor_availability')
        .select('start_time, end_time')
        .eq('mentor_id', mentorId)
        .eq('day_of_week', dayOfWeek)
        .eq('is_active', true);

      if (error) throw error;

      if (!availability || availability.length === 0) {
        setAvailableSlots([]);
        return;
      }

      // Convert mentor's availability to mentee's timezone for display
      console.log('DEBUG: Converting slots - Mentor TZ:', mentorTimezone, 'Mentee TZ:', menteeTimezone);

      const convertedSlots = convertAvailabilitySlotsToTimezone(
        availability.map(slot => ({
          start_time: slot.start_time,
          end_time: slot.end_time
        })),
        date,
        mentorTimezone,
        menteeTimezone
      );

      console.log('DEBUG: Converted slots:', convertedSlots);

      // Generate time slots from converted availability
      const slots: TimeSlot[] = [];
      const sessionDuration = bookingPreferences?.session_duration || 60;
      const bufferTime = bookingPreferences?.buffer_time || 15;

      for (const slot of convertedSlots) {
        // Use the passed 'date' (not selectedDate) to avoid stale state
        let startTime = parse(slot.start_time, 'HH:mm', date);
        let endTime = parse(slot.end_time, 'HH:mm', date);
        
        // Handle midnight-spanning slots
        if (slot.crosses_midnight && endTime <= startTime) {
          endTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
        }
        
        let currentTime = startTime;
        while (currentTime < endTime) {
          const timeSlot = format(currentTime, 'HH:mm');
          slots.push({ time: timeSlot, available: true });
          
          currentTime = new Date(currentTime.getTime() + (sessionDuration + bufferTime) * 60000);
        }
      }

      // Check for existing bookings on this date
      const { data: existingBookings } = await supabase
        .from('session_bookings')
        .select('start_time, end_time, booked_date')
        .eq('mentor_id', mentorId)
        .eq('booked_date', dateString)
        .neq('status', 'cancelled');

      // Mark booked slots as unavailable (compare against UTC-converted times)
      if (existingBookings) {
        existingBookings.forEach(booking => {
          // Convert UTC booking time to mentee timezone for comparison
          const bookingLocal = convertFromUTC(
            booking.booked_date,
            booking.start_time,
            menteeTimezone
          );
          const slotIndex = slots.findIndex(slot => slot.time === bookingLocal.localTime);
          if (slotIndex >= 0) {
            slots[slotIndex].available = false;
          }
        });
      }

      console.log('DEBUG: Generated time slots:', slots.length, 'slots');

      // Check minimum notice requirement - applies to ALL dates
      const now = new Date();
      const minimumNoticeHours = bookingPreferences?.minimum_notice_hours || 24;
      const minimumBookingTime = new Date(now.getTime() + minimumNoticeHours * 60 * 60 * 1000);

      slots.forEach(slot => {
        const slotDateTime = new Date(date);
        const [hours, minutes] = slot.time.split(':');
        slotDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        if (slotDateTime < minimumBookingTime) {
          slot.available = false;
        }
      });

      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast({
        title: "Error",
        description: "Failed to load available time slots.",
        variant: "destructive",
      });
    }
  };

  const handleBookSession = async () => {
    if (!selectedDate || !selectedTime) return;

    // Validate subscription restrictions
    if (subscriptionInfo.isSubscription) {
      if (!subscriptionInfo.isActive) {
        toast({
          title: "Booking Failed",
          description: "Your subscription is inactive. Please renew to book sessions.",
          variant: "destructive",
        });
        return;
      }
      
      if (subscriptionInfo.endDate && selectedDate > new Date(subscriptionInfo.endDate)) {
        toast({
          title: "Booking Failed", 
          description: "Selected date is beyond your subscription period.",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);
    try {
      console.log('DEBUG: Starting booking process for applicationId:', applicationId);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

    // Calculate end time in mentee's local time first
    const sessionDuration = bookingPreferences?.session_duration || 60;
    const [hours, minutes] = selectedTime.split(':');
    const menteeLocalStartDateTime = new Date(selectedDate);
    menteeLocalStartDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const menteeLocalEndDateTime = new Date(menteeLocalStartDateTime.getTime() + sessionDuration * 60000);

    // ⚠️ CRITICAL: Convert mentee's local time to UTC for storage
    const startUTC = convertToUTC(
      selectedDate,
      selectedTime,
      menteeTimezone
    );

    const endUTC = convertToUTC(
      selectedDate,
      format(menteeLocalEndDateTime, 'HH:mm'),
      menteeTimezone
    );

    console.log('DEBUG: Booking creation - Mentee local time:', selectedTime, 'in', menteeTimezone);
    console.log('DEBUG: Booking creation - Converted to UTC:', startUTC.utcTime, 'on', startUTC.utcDate);

    // Create session booking with UTC times
    const booking = {
      mentor_id: mentorId,
      mentee_id: user.id,
      application_id: applicationId,
      booked_date: startUTC.utcDate,  // ✅ UTC date
      start_time: startUTC.utcTime,    // ✅ UTC time
      end_time: endUTC.utcTime,        // ✅ UTC time
      status: (bookingPreferences?.auto_approve_bookings ? 'confirmed' : 'pending') as 'pending' | 'confirmed'
    };

    console.log('DEBUG: Creating session booking with UTC times:', booking);

      console.log('DEBUG: Creating session booking:', booking);
      const result = await createSessionBooking(booking);
      if (!result.success) throw new Error('Failed to create booking');

      // Notify mentor about new booking
      const { data: menteeProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .single();

      const menteeDisplayName = menteeProfile?.first_name && menteeProfile?.last_name 
        ? `${menteeProfile.first_name} ${menteeProfile.last_name}`.trim()
        : "A mentee";

      const { error: mentorNotificationError } = await supabase.from('notifications').insert({
        receiver_id: mentorId,
        type: 'booking_created',
        content: `📅 ${menteeDisplayName} has scheduled a session with you`,
        link: '/mentor-dashboard'
      });

      if (mentorNotificationError) {
        console.error('Error creating mentor notification:', mentorNotificationError);
      }

      // Check if this is a subscription application
      const isSubscription = await isSubscriptionApplication(applicationId);
      console.log('DEBUG: Is subscription application:', isSubscription);

      if (isSubscription) {
        // For subscription applications, the database triggers will handle counter updates
        console.log('DEBUG: Processing subscription booking');
        
        toast({
          title: "Session Booked",
          description: `Your subscription session is ${booking.status === 'confirmed' ? 'confirmed' : 'pending approval'} for ${format(selectedDate, 'PPP')} at ${selectedTime}.`,
        });

        // Dispatch event to update dashboards
        dispatchSubscriptionBookingUpdate(applicationId, mentorId, user.id);
      } else {
        // For regular applications, update application status
        console.log('DEBUG: Processing regular application booking');
        const { error: appError } = await supabase
          .from('applications')
          .update({ status: 'paid' })
          .eq('id', applicationId);

        if (appError) throw appError;

        toast({
          title: "Session Booked",
          description: `Your session is ${booking.status === 'confirmed' ? 'confirmed' : 'pending approval'} for ${format(selectedDate, 'PPP')} at ${selectedTime}.`,
        });
      }

      onBookingComplete();
    } catch (error) {
      console.error('Error booking session:', error);
      toast({
        title: "Booking Failed",
        description: "Failed to book session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const maxDate = addDays(new Date(), bookingPreferences?.advance_booking_days || 30);
  
  // For subscription applications, limit max date to subscription end date
  const effectiveMaxDate = subscriptionInfo.isSubscription && subscriptionInfo.endDate 
    ? new Date(Math.min(maxDate.getTime(), new Date(subscriptionInfo.endDate).getTime()))
    : maxDate;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Select Date & Time
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-medium mb-3">Choose a Date</h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => {
                const dateString = format(date, 'yyyy-MM-dd');
                
                // Block past dates and blocked dates
                if (date < startOfDay(new Date()) || blockedDates.includes(dateString)) {
                  return true;
                }
                
                // For subscriptions, use current period boundaries
                if (subscriptionInfo.isSubscription && currentPeriod) {
                  const checkDate = startOfDay(date);
                  const periodEnd = startOfDay(new Date(currentPeriod.periodEnd));
                  
                  // Disable dates after current period end
                  if (checkDate > periodEnd) {
                    return true;
                  }
                } else if (subscriptionInfo.isSubscription && subscriptionInfo.endDate) {
                  // Fallback to overall subscription end date
                  if (date > startOfDay(new Date(subscriptionInfo.endDate))) {
                    return true;
                  }
                } else {
                  // For non-subscription, use advance booking days
                  if (date > effectiveMaxDate) {
                    return true;
                  }
                }
                
                return false;
              }}
              className="rounded-md border"
            />
            {subscriptionInfo.isSubscription && subscriptionInfo.endDate && (
              <div className="mt-2 text-sm text-muted-foreground">
                <p>Subscription valid until: {format(new Date(subscriptionInfo.endDate), 'PPP')}</p>
                {!subscriptionInfo.isActive && (
                  <p className="text-destructive">⚠️ Subscription is inactive</p>
                )}
              </div>
            )}
          </div>

          {selectedDate && (
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Available Times for {format(selectedDate, 'PPP')}
              </h3>
              
              {availableSlots.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No available time slots for this date
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={selectedTime === slot.time ? "default" : "outline"}
                      size="sm"
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      className="text-xs"
                    >
                      {slot.time}
                      {!slot.available && (
                        <Badge variant="secondary" className="ml-1 text-xs">
                          Unavailable
                        </Badge>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedDate && selectedTime && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Booking Summary</h4>
              <p className="text-blue-700">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime}
              </p>
              {bookingPreferences && (
                <>
                  <p className="text-sm text-blue-600 mt-1">
                    Duration: {bookingPreferences.session_duration} minutes
                  </p>
                  {!bookingPreferences.auto_approve_bookings && (
                    <p className="text-sm text-blue-600">
                      Note: This booking requires mentor approval
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <Button 
            onClick={handleBookSession}
            disabled={!selectedDate || !selectedTime || loading}
            className="w-full"
          >
            {loading ? "Booking..." : "Confirm Booking"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingCalendar;
